import React, {useState, useEffect, useRef} from "react";
import {Col, Table, Input, Switch, Button} from "antd";
import {useFormik} from "formik";
import {useHistory} from "react-router-dom";
import {useTranslation} from "react-i18next";

import {SubCompanyContainer, FilterContainer, SelectField} from "./optionsStyles";
import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {CityFilter} from "../../_defaultComponents/FilterComponents";
import {EmptyMessage} from "../../_defaultComponents/EmptyMessage/EmptyMessage";

const {Column} = Table;

export const ReadyOrderNotification = ({companyId}) => {
    const stores = useStore();
    const history = useHistory();
    const {t} = useTranslation();

    const [currentPage, setCurrentPage] = useState(1);
    const [isCompanyFormChanged, setIsCompanyFormChanged] = useState(false);
    const [companyFormData, setCompanyFormData] = useState();
    const [city, setCity] = useState("all");
    const [searchText, setSearchText] = useState("");
    const [text, setText] = useState("");
    const [isSubCompanySubmitting, setIsSubCompanySubmitting] = useState(false);
    const [companyRecall, setCompanyRecall] = useState(false);
    const [subCompanyRecall, setSubCompanyRecall] = useState(false);
    const [upperGroup, setUpperGroup] = useState("");
    const [regionGroups, setRegionGroups] = useState([]);
    const [bulkSubmitting, setBulkSubmitting] = useState(false);

    const companyRecallRef = useRef(companyRecall);
    companyRecallRef.current = companyRecall;
    const subCompanyRecallRef = useRef(subCompanyRecall);
    subCompanyRecallRef.current = subCompanyRecall;

    const [companyLoading, companyData, setCompanyData, setCompanyLoading] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.companyStore.getCompany.bind(null, {
            companyId,
            withSubCompanies: false,
            vehicleType: null
        }),
        dependencies: [companyRecall]
    });

    const [subCompanyLoading, subCompanyData, setSubCompanyData,] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.companyStore.getSubCompanyList.bind(null, {
            companyId: companyId,
            limit: 10,
            skip: (currentPage - 1) * 10,
            activity: "enabled",
            vehicleType: null,
            city,
            searchText,
            upperGroup,
            regionGroups: regionGroups?.join(".")
        }),
        dependencies: [
            currentPage,
            subCompanyRecall,
            city,
            searchText,
            upperGroup,
            regionGroups
        ]
    });

    const [upperGroupsLoading, upperGroups, setUpperGroups, setUpperGroupsLoading] = useFetch({
        fetchFn: stores.regionStore.getUpperGroupsByCompanyIdAndCity.bind(null, {
            companyId,
            city
        }),
        dependencies: [companyId, city],
        initialCall: false
    });

    const formik = useFormik({
        initialValues: {
        options: {
            readyOrderNotification: {
                isActive: companyData?.data?.options?.readyOrderNotification?.isActive || false,
            }
        },
        ...(companyData && companyData.data)
    },
        enableReinitialize: true,
        validateOnChange: true,
        onSubmit: async values => {
            setCompanyLoading(true);

            let filters = {};
            filters.searchText = searchText;
            filters.city = city;
            filters.upperGroup = upperGroup;
            filters.regionGroups = regionGroups;

            if (Object.keys(filters).length > 0) {
                values.filters = filters;
            }

            if (values.isActive) {
                setSubCompanyRecall(!subCompanyRecallRef.current);
            }
            setCompanyRecall(!companyRecallRef.current);
            setCompanyLoading(false);
            setIsCompanyFormChanged(false);
        }
    });

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue
    } = formik;

    useEffect(() => {
        if (values && !isCompanyFormChanged) {
            setCompanyFormData(values);
        } else if (values && JSON.stringify(companyFormData) !== JSON.stringify(values)) {
            setIsCompanyFormChanged(true);
        } else {
            setIsCompanyFormChanged(false);
        }
    }, [values]);



    const onBulkApplySubmit = async () => {
        setBulkSubmitting(true);
        try {
            const subCompanyIds = subCompanyData?.rows?.map(sc => sc._id) || [];
            const promises = subCompanyIds.map(subCompanyId => {
                return stores.companyStore.updateSubCompanyReadyOrderNotificationSettings({
                    subCompanyId,
                    readyOrder: true
                });
            });

            const results = await Promise.all(promises);
            const allSuccess = results.every(result => result);
            
            if (allSuccess) {
                setSubCompanyRecall(!subCompanyRecallRef.current);
                stores.appStore.showSuccessMessage({
                    content: t("Tüm alt firmalara başarıyla uygulandı")
                });
            }
        } catch (error) {
            console.error('Error updating bulk ready order notification settings:', error);
            stores.appStore.openErrorModal({
                content: t("Toplu uygulama sırasında bir hata oluştu")
            });
        }
        setBulkSubmitting(false);
    };

    return (
        <div data-testid="readyOrderNotificationScreen">
            <h2 data-testid="companyName">{companyData?.data?.companyName}</h2>
            <Col span={12}>
                <FilterContainer type="flex" gutter={[20, 20]}>
                    <Col span={6}>
                        <CityFilter
                            value={city}
                            onChange={value => {
                                setCurrentPage(1);
                                setCity(value);
                                setRegionGroups([]);
                            }}
                        />
                    </Col>
                    <Col span={6}>
                        <h5>{t("fields.upperGroup", {ns: "common"})}</h5>
                        <SelectField
                            value={upperGroup}
                            onChange={value => {
                                setCurrentPage(1);
                                setUpperGroup(value);
                            }}
                            disabled={!city}
                            notFoundContent={t("filters.dataNotFound", {ns: "common"})}
                            placeholder={t("filters.addFilter", {ns: "common"})}
                            optionFilterProp="children"
                            data-testid="upperGroupSelect"
                            filterOption={(input, option) =>
                                option.children
                                    .toString()
                                    .toLowerCase()
                                    .indexOf(input.toLowerCase()) >= 0
                            }
                            filterSort={(optionA, optionB) =>
                                optionA.children
                                    .toString()
                                    .toLowerCase()
                                    .localeCompare(optionB.children.toString().toLowerCase())
                            }
                        >
                            <SelectField.Option value="" data-testid="upperGroupSelect_option">
                                {t("filters.all", {ns: "common"})}
                            </SelectField.Option>
                            {upperGroups.map(upperGroup => (
                                <SelectField.Option
                                    key={upperGroup.group}
                                    value={upperGroup.group}
                                    data-testid="upperGroupSelect_option"
                                >
                                    {upperGroup.group}
                                </SelectField.Option>
                            ))}
                        </SelectField>
                    </Col>
                    <Col span={6}>
                        <h5>{t("fields.regionGroup", {ns: "common"})}</h5>
                        <SelectField
                            value={regionGroups}
                            onChange={(value, children) => {
                                setCurrentPage(1);
                                setRegionGroups(value);
                                console.log("value", value);
                                console.log("children", children);
                            }}
                            disabled={!city || upperGroup === ""}
                            notFoundContent={t("filters.dataNotFound", {ns: "common"})}
                            mode="multiple"
                            placeholder={t("filters.addFilter", {ns: "common"})}
                            optionFilterProp="children"
                            data-testid="regionGroupSelect"
                            filterOption={(input, option) =>
                                option.children
                                    .toString()
                                    .toLowerCase()
                                    .indexOf(input.toLowerCase()) >= 0
                            }
                            filterSort={(optionA, optionB) =>
                                optionA.children
                                    .toString()
                                    .toLowerCase()
                                    .localeCompare(optionB.children.toString().toLowerCase())
                            }
                        >
                            {upperGroups
                                .find(c => c.group == upperGroup)
                                ?.regiongroups.map(regionGroupItem => (
                                    <SelectField.Option
                                        key={regionGroupItem._id}
                                        value={regionGroupItem._id}
                                        data-testid="regionGroupSelect_option"
                                    >
                                        {regionGroupItem.name}
                                    </SelectField.Option>
                                ))}
                        </SelectField>
                    </Col>
                    <Col span={6}>
                        <h5>{t("filters.search", {ns: "common"})}</h5>
                        <Input.Search
                            data-testid="searchInput"
                            value={text}
                            onSearch={value => {
                                setCurrentPage(1);
                                setSearchText(value);
                            }}
                            onChange={event => {
                                setText(event.target.value);
                            }}
                        />
                    </Col>
                </FilterContainer>
            </Col>
            <SubCompanyContainer>
                <Button
                    type="primary"
                    onClick={onBulkApplySubmit}
                    loading={bulkSubmitting}
                    style={{ marginBottom: 16 }}
                    data-testid="bulkApplyButton"
                >
                    {t("Tüm Alt Firmalara Uygula")}
                </Button>
                <Table
                    rowKey="_id"
                    data-testid="subCompaniesTable"
                    dataSource={subCompanyData?.rows}
                    loading={subCompanyLoading}
                    pagination={{
                        total: subCompanyData.dataCount,
                        current: currentPage,
                        onChange: async page => {
                            setCurrentPage(page);
                            history.push(
                                "/companies/ready-order-notification-options/" + companyId + "?page=" + page
                            );
                        },
                        hideOnSinglePage: true,
                        showSizeChanger: false
                    }}
                    locale={{
                        emptyText: <EmptyMessage />
                    }}
                >
                    <Column
                        dataIndex="name"
                        title={t("fields.subcompany", {ns: "common"})}
                        render={text => <span data-textid="name">{text}</span>}
                    />
                    <Column
                        dataIndex="city"
                        title={t("fields.city", {ns: "common"})}
                        render={text => <span data-textid="city">{text}</span>}
                    />
                    <Column
                        dataIndex={"upperRegionGroup"}
                        title={t("fields.upperGroup", {ns: "common"})}
                        render={text => <span data-textid="upperRegionGroup">{text}</span>}
                    />
                    <Column
                        dataIndex={"regionGroup"}
                        key={"regionGroup"}
                        title={t("fields.regionGroup", {ns: "common"})}
                        render={text => <span data-testid="regionGroup">{console.log(text)}</span>}
                    />
                    <Column
                        dataIndex="readyOrderNotification"
                        title="Hazır Sipariş Bildirimi"
                        render={(text, record, index) => (
                            <Switch
                                data-testid="isActiveSwitch"
                                checked={Boolean(subCompanyData.rows[index]?.isOrderPreparedActive)}
                                loading={isSubCompanySubmitting}
                                onChange={async e => {
                                    const subCompany = subCompanyData.rows[index];
                                    setIsSubCompanySubmitting(true);
                                    try {
                                        const result = await stores.companyStore.updateSubCompanyReadyOrderNotificationSettings({
                                            subCompanyId: subCompany._id,
                                            readyOrder: e
                                        });
                                        if (result) {
                                            setSubCompanyData(prevData => {
                                                const newData = {...prevData};
                                                const newItem = {
                                                    ...newData.rows[index],
                                                    isOrderPreparedActive: e
                                                };
                                                newData.rows[index] = newItem;
                                                return newData;
                                            });
                                        }
                                    } catch (error) {
                                        stores.appStore.showErrorMessage({
                                            content: error.message
                                        });
                                    } finally {
                                        setIsSubCompanySubmitting(false);
                                    }
                                }}
                            />
                        )}
                    />
                </Table>
            </SubCompanyContainer>
        </div>
    );
};